import { useEffect, useState, useCallback, useRef } from "react";
import { getAnnouncements, createAnnouncement, updateAnnouncement, deleteAnnouncement } from "../../api/announcements";
import { getTournamentList } from "../../api/tournaments";
import { getActiveSeasons } from "../../api/seasons";
import { ISeason } from "../../models/ISeason";
import { Plus, Pencil, Trash, X } from "lucide-react";
import { storage } from "../../services/firebase";
import { ref, uploadBytes, getDownloadURL, listAll } from "firebase/storage";
import moment from "moment";
import "./styles.scss";
import { useAppDispatch } from "../../store/store";
import { setGeneralAlertMessage } from "../../store/slices/generalSlice";
import {
  Button,
  Card,
  Checkbox,
  ConfirmDialog,
  DateInput,
  Dialog,
  EmptyState,
  Field,
  FormGrid,
  IconButton,
  Input,
  Loader,
  PageHeader,
  SearchInput,
  Select,
  TableWrap,
  Tag,
  Textarea,
  UploadBox,
} from "../../ui";

interface IAnnouncement {
  id: string;
  title: string;
  description?: string;
  imageUrl?: string;
  tournamentId?: string;
  tournamentName?: string;
  linkType?: string;
  linkData?: string;
  isActive: boolean;
  startDate?: string;
  endDate?: string;
  displayOrder?: number;
}

interface ITournamentOption {
  id: string;
  name: string;
  startDate?: string;
  endDate?: string;
}

interface AnnouncementFormData {
  title: string;
  description: string;
  imageUrl: string;
  linkType: string; // "" | "tournament" | "court-reservation" | "rankings" | "gallery"
  tournamentId: string;
  linkData: string; // JSON string for extra params
  isActive: boolean;
  startDate: string;
  endDate: string;
  displayOrder: number;
}

const LINK_TYPES = [
  { value: "", label: "None (No navigation)" },
  { value: "tournament", label: "Tournament" },
  { value: "court-reservation", label: "Court Reservations" },
  { value: "rankings", label: "Rankings" },
  { value: "gallery", label: "Gallery" },
];

// The line under a phone card's title: the tournament, or where else the announcement links to
const linkTargetOf = (a: IAnnouncement): string => {
  if (a.tournamentName) return a.tournamentName;
  if (!a.linkType || a.linkType === "tournament") return "";
  const label = LINK_TYPES.find((lt) => lt.value === a.linkType)?.label || "";
  return a.linkType === "gallery" && a.linkData ? `${label} · ${a.linkData}` : label;
};

const defaultFormData: AnnouncementFormData = {
  title: "",
  description: "",
  imageUrl: "",
  linkType: "",
  tournamentId: "",
  linkData: "",
  isActive: true,
  startDate: "",
  endDate: "",
  displayOrder: 0,
};

const Announcements = () => {
  const dispatch = useAppDispatch();
  const [announcements, setAnnouncements] = useState<IAnnouncement[]>([]);
  const [tournaments, setTournaments] = useState<ITournamentOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Dialog state
  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState<IAnnouncement | null>(null);
  const [announcementToDelete, setAnnouncementToDelete] = useState<IAnnouncement | null>(null);
  const [formData, setFormData] = useState<AnnouncementFormData>(defaultFormData);
  const [tournamentSearch, setTournamentSearch] = useState("");
  const [showTournamentDropdown, setShowTournamentDropdown] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [seasons, setSeasons] = useState<ISeason[]>([]);
  const [selectedSport, setSelectedSport] = useState("");
  const [selectedSeasonId, setSelectedSeasonId] = useState("");
  const [galleryTournaments, setGalleryTournaments] = useState<string[]>([]);
  const [gallerySearch, setGallerySearch] = useState("");

  const fetchAnnouncements = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAnnouncements();
      const items = res?.data?.data || res?.data || [];
      setAnnouncements(Array.isArray(items) ? items : []);
    } catch (err: any) {
      console.error("Error fetching announcements", err);
      setAnnouncements([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const searchTournaments = useCallback(async (search: string) => {
    try {
      const { data } = await getTournamentList(1, 20, search, "name", "asc");
      const items = data?.items || [];
      setTournaments(items.map((t: any) => ({ id: t.id, name: t.name, startDate: t.startDate, endDate: t.endDate })));
    } catch (err: any) {
      console.error("Error fetching tournaments", err);
    }
  }, []);

  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!showTournamentDropdown) return;
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      searchTournaments(tournamentSearch);
    }, 300);
    return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
  }, [tournamentSearch, showTournamentDropdown, searchTournaments]);

  useEffect(() => {
    document.title = "Announcements";
    fetchAnnouncements();
    getActiveSeasons().then((res: any) => {
      const items = res?.data?.data || res?.data || [];
      setSeasons(Array.isArray(items) ? items : []);
    }).catch(() => {});
    listAll(ref(storage, "tournament-photos")).then((result) => {
      setGalleryTournaments(result.prefixes.map((p) => p.name).sort());
    }).catch(() => {});
  }, [fetchAnnouncements]);

  const handleAdd = () => {
    setEditingAnnouncement(null);
    setFormData(defaultFormData);
    setSelectedSport("");
    setSelectedSeasonId("");
    setIsFormDialogOpen(true);
  };

  const handleEdit = (announcement: IAnnouncement) => {
    setEditingAnnouncement(announcement);
    setFormData({
      title: announcement.title || "",
      description: announcement.description || "",
      imageUrl: announcement.imageUrl || "",
      linkType: announcement.linkType || (announcement.tournamentId ? "tournament" : ""),
      tournamentId: announcement.tournamentId || "",
      linkData: announcement.linkData || "",
      isActive: announcement.isActive ?? true,
      startDate: announcement.startDate ? moment(announcement.startDate).format("YYYY-MM-DD") : "",
      endDate: announcement.endDate ? moment(announcement.endDate).format("YYYY-MM-DD") : "",
      displayOrder: announcement.displayOrder ?? 0,
    });
    if (announcement.linkType === "rankings" && announcement.linkData) {
      try {
        const parsed = JSON.parse(announcement.linkData);
        setSelectedSport(parsed.sport || "");
        setSelectedSeasonId(parsed.seasonId || "");
      } catch { setSelectedSport(""); setSelectedSeasonId(""); }
    } else {
      setSelectedSport("");
      setSelectedSeasonId("");
    }
    setIsFormDialogOpen(true);
  };

  const handleDeleteClick = (announcement: IAnnouncement) => {
    setAnnouncementToDelete(announcement);
    setIsDeleteDialogOpen(true);
  };

  const handleImageUpload = async (file: File) => {
    if (!file.type.startsWith("image/")) {
      dispatch(setGeneralAlertMessage({ type: "error", message: "Please select an image file" }));
      return;
    }
    setUploadingImage(true);
    try {
      const fileName = `announcements/${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g, "")}`;
      const storageRef = ref(storage, fileName);
      const snapshot = await uploadBytes(storageRef, file);
      const url = await getDownloadURL(snapshot.ref);
      handleFormChange("imageUrl", url);
    } catch (err: any) {
      console.error("Upload error:", err);
      dispatch(setGeneralAlertMessage({ type: "error", message: err?.message || "Failed to upload image" }));
    } finally {
      setUploadingImage(false);
    }
  };

  const handleFormChange = (field: keyof AnnouncementFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!formData.title.trim()) return;

    setSaving(true);
    try {
      const payload: any = {
        title: formData.title.trim(),
        description: formData.description.trim() || undefined,
        imageUrl: formData.imageUrl.trim() || undefined,
        tournamentId: formData.linkType === "tournament" && formData.tournamentId ? formData.tournamentId : "00000000-0000-0000-0000-000000000000",
        linkType: formData.linkType || "",
        linkData: formData.linkData || "",
        isActive: formData.isActive,
        startDate: formData.startDate || undefined,
        endDate: formData.endDate || undefined,
        displayOrder: formData.displayOrder,
      };

      if (editingAnnouncement) {
        await updateAnnouncement(editingAnnouncement.id, payload);
        dispatch(setGeneralAlertMessage({ message: "Announcement updated successfully", type: "success" }));
      } else {
        await createAnnouncement(payload);
        dispatch(setGeneralAlertMessage({ message: "Announcement created successfully", type: "success" }));
      }

      setIsFormDialogOpen(false);
      setEditingAnnouncement(null);
      await fetchAnnouncements();
    } catch (err: any) {
      dispatch(
        setGeneralAlertMessage({
          message: err?.message || "Failed to save announcement",
          type: "error",
        })
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!announcementToDelete) return;

    setDeleting(true);
    try {
      await deleteAnnouncement(announcementToDelete.id);
      dispatch(setGeneralAlertMessage({ message: "Announcement deleted successfully", type: "success" }));
      setIsDeleteDialogOpen(false);
      setAnnouncementToDelete(null);
      await fetchAnnouncements();
    } catch (err: any) {
      dispatch(
        setGeneralAlertMessage({
          message: err?.message || "Failed to delete announcement",
          type: "error",
        })
      );
    } finally {
      setDeleting(false);
    }
  };



  // Keep the dialogs' words steady while they animate closed
  const formTitle = useRef("Add Announcement");
  const formSaveLabel = useRef("Create");
  if (isFormDialogOpen) {
    formTitle.current = editingAnnouncement ? "Edit Announcement" : "Add Announcement";
    formSaveLabel.current = editingAnnouncement ? "Update" : "Create";
  }
  const lastToDelete = useRef<IAnnouncement | null>(null);
  if (announcementToDelete) lastToDelete.current = announcementToDelete;

  const closeForm = () => {
    if (!saving) setIsFormDialogOpen(false);
  };

  const filteredGalleryTournaments = galleryTournaments.filter((name) =>
    name.toLowerCase().includes(gallerySearch.toLowerCase())
  );

  return (
    <div className="announcements-page">
      <PageHeader
        title="Announcements"
        subtitle={`${announcements.length} ${announcements.length === 1 ? "Announcement" : "Announcements"}`}
        actions={
          <Button icon={<Plus size={18} />} onClick={handleAdd}>
            Add Announcement
          </Button>
        }
      />

      {loading && announcements.length === 0 ? (
        <Loader />
      ) : announcements.length === 0 ? (
        <Card padding="none">
          <EmptyState title="No announcements found." />
        </Card>
      ) : (
        <>
          <TableWrap className="announcements-table-wrap">
            <table className="ui-table announcements-table">
              <thead>
                <tr>
                  <th>Image</th>
                  <th>Title</th>
                  <th>Tournament</th>
                  <th>Status</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Order</th>
                  <th className="ui-table__actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {announcements.map((announcement) => (
                  <tr key={announcement.id}>
                    <td>
                      {announcement.imageUrl ? (
                        <img src={announcement.imageUrl} alt={announcement.title} className="announcement-thumbnail" />
                      ) : (
                        <div className="announcement-no-image">N/A</div>
                      )}
                    </td>
                    <td className="announcement-title">{announcement.title}</td>
                    <td>{announcement.tournamentName || "-"}</td>
                    <td>
                      <Tag tone={announcement.isActive ? "ok" : "danger"}>
                        {announcement.isActive ? "Active" : "Inactive"}
                      </Tag>
                    </td>
                    <td className="nowrap">
                      {announcement.startDate ? moment(announcement.startDate).format("DD MMM, YYYY") : "-"}
                    </td>
                    <td className="nowrap">
                      {announcement.endDate ? moment(announcement.endDate).format("DD MMM, YYYY") : "-"}
                    </td>
                    <td>{announcement.displayOrder ?? "-"}</td>
                    <td className="ui-table__actions">
                      <IconButton label="Edit" icon={<Pencil size={16} />} onClick={() => handleEdit(announcement)} />
                      <IconButton
                        label="Delete"
                        variant="danger"
                        icon={<Trash size={16} />}
                        onClick={() => handleDeleteClick(announcement)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>

          {/* Phones: the same rows as cards (the table above is hidden under 768px) */}
          <div className="announcement-cards">
            {announcements.map((announcement) => {
              const target = linkTargetOf(announcement);
              const hasDates = !!(announcement.startDate || announcement.endDate);
              return (
                <Card key={announcement.id} padding="sm" className="announcement-card">
                  {announcement.imageUrl ? (
                    <img src={announcement.imageUrl} alt={announcement.title} className="announcement-card__image" />
                  ) : (
                    <div className="announcement-card__image announcement-no-image">N/A</div>
                  )}
                  <div className="announcement-card__body">
                    <p className="announcement-card__title">{announcement.title}</p>
                    {target && <p className="announcement-card__line">{target}</p>}
                    {hasDates && (
                      <p className="announcement-card__line announcement-card__dates">
                        {announcement.startDate ? moment(announcement.startDate).format("DD MMM, YYYY") : "∞"}
                        {" – "}
                        {announcement.endDate ? moment(announcement.endDate).format("DD MMM, YYYY") : "∞"}
                      </p>
                    )}
                    <div className="announcement-card__foot">
                      <Tag tone={announcement.isActive ? "ok" : "danger"}>
                        {announcement.isActive ? "Active" : "Inactive"}
                      </Tag>
                      <span className="announcement-card__order">Order {announcement.displayOrder ?? "-"}</span>
                    </div>
                  </div>
                  <div className="announcement-card__actions">
                    <IconButton
                      size="sm"
                      label="Edit"
                      icon={<Pencil size={16} />}
                      onClick={() => handleEdit(announcement)}
                    />
                    <IconButton
                      size="sm"
                      label="Delete"
                      variant="danger"
                      icon={<Trash size={16} />}
                      onClick={() => handleDeleteClick(announcement)}
                    />
                  </div>
                </Card>
              );
            })}
          </div>
        </>
      )}

      {/* Add/Edit Dialog */}
      <Dialog
        open={isFormDialogOpen}
        onClose={closeForm}
        title={formTitle.current}
        busy={saving}
        bodyClassName="announcement-form"
        footer={
          <>
            <Button variant="ghost" onClick={closeForm} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} loading={saving} disabled={!formData.title.trim()}>
              {formSaveLabel.current}
            </Button>
          </>
        }
      >
        <Field label="Title" required>
          {(fieldId) => (
            <Input
              id={fieldId}
              type="text"
              value={formData.title}
              onChange={(e) => handleFormChange("title", e.target.value)}
              placeholder="Enter announcement title"
            />
          )}
        </Field>

        <Field label="Description">
          {(fieldId) => (
            <Textarea
              id={fieldId}
              value={formData.description}
              onChange={(e) => handleFormChange("description", e.target.value)}
              placeholder="Enter description"
              rows={3}
            />
          )}
        </Field>

        <Field label="Image (Square 1:1)">
          <UploadBox
            className="announcement-image-upload"
            aspect="square"
            previewUrl={formData.imageUrl || null}
            onPick={handleImageUpload}
            onRemove={() => handleFormChange("imageUrl", "")}
            uploading={uploadingImage}
            accept="image/png,image/jpeg,image/webp,image/gif"
            label="Click to upload image"
            hint="Recommended: Square (1:1 ratio)"
          />
        </Field>

        <Field label="Link To">
          {(fieldId) => (
            <Select
              id={fieldId}
              block
              value={formData.linkType}
              onChange={(e) => {
                handleFormChange("linkType", e.target.value);
                handleFormChange("tournamentId", "");
                handleFormChange("linkData", "");
              }}
            >
              {LINK_TYPES.map((lt) => (
                <option key={lt.value} value={lt.value}>
                  {lt.label}
                </option>
              ))}
            </Select>
          )}
        </Field>

        {formData.linkType === "gallery" && (
          <Field label="Gallery for Tournament">
            {(fieldId) => (
              <>
                <SearchInput
                  id={fieldId}
                  block
                  placeholder="Search gallery tournaments..."
                  value={gallerySearch}
                  onChange={setGallerySearch}
                />
                <div className="pick-list gallery-list">
                  {filteredGalleryTournaments.map((name) => (
                    <button
                      type="button"
                      key={name}
                      className={`pick-list__item ${formData.linkData === name ? "is-selected" : ""}`}
                      onClick={() => handleFormChange("linkData", name)}
                    >
                      {name}
                    </button>
                  ))}
                  {filteredGalleryTournaments.length === 0 && (
                    <div className="pick-list__empty">No gallery tournaments found</div>
                  )}
                </div>
              </>
            )}
          </Field>
        )}

        {formData.linkType === "tournament" && (
          <Field label="Tournament" className="tournament-search-group">
            {(fieldId) =>
              formData.tournamentId ? (
                <div className="selected-tournament">
                  <span>{tournaments.find((t) => t.id === formData.tournamentId)?.name || "Selected"}</span>
                  <IconButton
                    size="sm"
                    variant="danger"
                    label="Clear tournament"
                    icon={<X size={14} />}
                    onClick={() => handleFormChange("tournamentId", "")}
                  />
                </div>
              ) : (
                <>
                  <SearchInput
                    id={fieldId}
                    block
                    placeholder="Search tournaments..."
                    value={tournamentSearch}
                    onChange={setTournamentSearch}
                    onFocus={() => setShowTournamentDropdown(true)}
                  />
                  {showTournamentDropdown && (
                    <div className="pick-list tournament-dropdown">
                      {tournaments.map((t) => (
                        <button
                          type="button"
                          key={t.id}
                          className="pick-list__item"
                          onClick={() => {
                            handleFormChange("tournamentId", t.id);
                            setShowTournamentDropdown(false);
                            setTournamentSearch("");
                          }}
                        >
                          <span className="tournament-option-name">{t.name}</span>
                          {t.startDate && (
                            <span className="tournament-option-date">
                              {moment(t.startDate).format("DD MMM YYYY")}
                              {t.endDate && ` - ${moment(t.endDate).format("DD MMM YYYY")}`}
                            </span>
                          )}
                        </button>
                      ))}
                      {tournaments.length === 0 && tournamentSearch && (
                        <div className="pick-list__empty">No tournaments found</div>
                      )}
                    </div>
                  )}
                </>
              )
            }
          </Field>
        )}

        {formData.linkType === "rankings" && (
          <>
            <Field label="Sport">
              {(fieldId) => (
                <Select
                  id={fieldId}
                  block
                  value={selectedSport}
                  onChange={(e) => {
                    setSelectedSport(e.target.value);
                    setSelectedSeasonId("");
                    handleFormChange("linkData", e.target.value ? JSON.stringify({ sport: e.target.value }) : "");
                  }}
                >
                  <option value="">Select a sport</option>
                  {[...new Set(seasons.map((s) => s.typeName))].sort().map((sport) => (
                    <option key={sport} value={sport}>
                      {sport}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            {selectedSport && (
              <Field label="Season">
                {(fieldId) => (
                  <Select
                    id={fieldId}
                    block
                    value={selectedSeasonId}
                    onChange={(e) => {
                      setSelectedSeasonId(e.target.value);
                      handleFormChange(
                        "linkData",
                        JSON.stringify({
                          sport: selectedSport,
                          ...(e.target.value ? { seasonId: e.target.value } : {}),
                        })
                      );
                    }}
                  >
                    <option value="">All Seasons</option>
                    {seasons
                      .filter((s) => s.typeName === selectedSport)
                      .map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                  </Select>
                )}
              </Field>
            )}
          </>
        )}

        <Checkbox
          id="isActive"
          checked={formData.isActive}
          onChange={(checked) => handleFormChange("isActive", checked)}
          label="Active"
        />

        <FormGrid columns={2}>
          <Field label="Start Date">
            {(fieldId) => (
              <DateInput
                id={fieldId}
                block
                value={formData.startDate}
                onChange={(e) => handleFormChange("startDate", e.target.value)}
              />
            )}
          </Field>
          <Field label="End Date">
            {(fieldId) => (
              <DateInput
                id={fieldId}
                block
                value={formData.endDate}
                onChange={(e) => handleFormChange("endDate", e.target.value)}
              />
            )}
          </Field>
        </FormGrid>

        <Field label="Display Order">
          {(fieldId) => (
            <Input
              id={fieldId}
              type="number"
              value={formData.displayOrder}
              onChange={(e) => handleFormChange("displayOrder", parseInt(e.target.value) || 0)}
              min={0}
            />
          )}
        </Field>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={isDeleteDialogOpen && !!announcementToDelete}
        onClose={(confirmed) => {
          // A successful delete clears it itself; a failed one stays open, as before
          if (!confirmed) {
            setIsDeleteDialogOpen(false);
            setAnnouncementToDelete(null);
          }
        }}
        onConfirm={handleDeleteConfirm}
        loading={deleting}
        title="Delete Announcement"
        message={
          <>
            Are you sure you want to delete "<strong>{lastToDelete.current?.title}</strong>"? This action cannot be
            undone.
          </>
        }
        confirmLabel="Delete"
      />
    </div>
  );
};

export default Announcements;
